import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import { API_CONFIG } from '../config/config';

interface Business {
  business_id?: string;
  businessName?: string;
  [key: string]: any;
}

interface UserData {
  id?: string | number;
  user_id?: string | number;
  userId?: string | number;
  email?: string;
  userEmail?: string;
  user_email?: string;
  username?: string;
  userName?: string;
  user_name?: string;
  firstName?: string;
  first_name?: string;
  middleName?: string;
  middle_name?: string;
  lastName?: string;
  last_name?: string;
  full_name?: string | null;
  fullName?: string | null;
  name?: string | null;
  mobile?: string | null;
  pictureUrl?: string | null;
  picture_url?: string | null;
  image?: string | null;
  picture?: string | null;
  avatar?: string | null;
  businesses?: Business[];
  [key: string]: any;
}

interface NormalizedUser {
  id: string | number | null;
  username: string | null;
  email: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  full_name: string | null;
  mobile: string | null;
  pictureUrl: string | null;
  businesses: Business[];
  role?: string;
  [key: string]: any;
}

interface AuthContextType {
  user: NormalizedUser | null;
  userBusinessDetails: Business[] | null;
  permissions: string[];
  permissionLevel: string;
  hasPermission: (permission: string) => boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string, opts?: { redirectTo?: string }) => Promise<void>;
  isAdmin: boolean;
  register: (userData: any) => Promise<boolean>;
  logout: () => Promise<void>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  checkBackendAvailability: () => Promise<boolean>;
  updateUser: (userData: any) => void;
  handleGoogleAuth: (apiResponse: any) => Promise<UserData>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const normalizeUserData = (userData: UserData | null): NormalizedUser | null => {
  if (!userData) return null;

  const normalized: NormalizedUser = {
    id: userData.id || userData.user_id || userData.userId || null,
    username: userData.username || userData.userName || userData.user_name || null,
    email: userData.email || userData.userEmail || userData.user_email || null,
    firstName: userData.firstName || userData.first_name || null,
    middleName: userData.middleName || userData.middle_name || null,
    lastName: userData.lastName || userData.last_name || null,
    full_name: userData.full_name || userData.fullName || userData.name || null,
    mobile: userData.mobile || null,
    pictureUrl: userData.pictureUrl || userData.picture_url || userData.image || userData.picture || userData.avatar || null,
    businesses: userData.businesses || [],
    ...userData,
  };

  if (normalized.full_name && (!normalized.firstName && !normalized.lastName)) {
    const nameParts = normalized.full_name.split(' ');
    if (nameParts.length >= 1) normalized.firstName = nameParts[0];
    if (nameParts.length >= 2) normalized.lastName = nameParts[nameParts.length - 1];
    if (nameParts.length >= 3) normalized.middleName = nameParts.slice(1, nameParts.length - 1).join(' ');
  }

  if (!normalized.full_name && (normalized.firstName || normalized.lastName)) {
    normalized.full_name = [normalized.firstName, normalized.middleName, normalized.lastName]
      .filter(Boolean)
      .join(' ');
  }

  return normalized;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<NormalizedUser | null>(null);
  const [userBusinessDetails, setBusinessDetails] = useState<Business[] | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [permissionLevel, setPermissionLevel] = useState<string>('USER');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const applyPermissions = useCallback((data: any) => {
    const perms = data?.payload?.permissions || data?.permissions || [];
    setPermissions(Array.isArray(perms) ? perms : []);
    setPermissionLevel(data?.payload?.permissionLevel || data?.permissionLevel || 'USER');
  }, []);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const response: any = await api.get(API_CONFIG.ENDPOINTS.AUTH.ME);
      const userData = response?.data;
      if (!userData) {
        setUser(null);
        setBusinessDetails(null);
        setPermissions([]);
        setPermissionLevel('USER');
        return;
      }
      const normalized = normalizeUserData(userData);
      setUser(normalized);
      setBusinessDetails(normalized?.businesses || null);
      if (Array.isArray(userData.permissions)) {
        setPermissions(userData.permissions);
      }
      if (userData.permissionLevel) {
        setPermissionLevel(userData.permissionLevel);
      }
    } catch (error: any) {
      if (error?.response?.status === 401) {
        setUser(null);
        setBusinessDetails(null);
        setPermissions([]);
        setPermissionLevel('USER');
      }
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      if (location.pathname !== '/oauth-callback') {
        await fetchCurrentUser();
      }
      setLoading(false);
    };
    init();
  }, [fetchCurrentUser, location.pathname]);

  const checkBackendAvailability = async (): Promise<boolean> => {
    try {
      await api.get(API_CONFIG.ENDPOINTS.AUTH.HEALTH, {
        timeout: 5000,
      } as any);
      return true;
    } catch (error: any) {
      if (error.response) return true;
      return false;
    }
  };

  const login = async (email: string, password: string, opts?: { redirectTo?: string }): Promise<void> => {
    try {
      setError(null);
      setLoading(true);

      const isAvailable = await checkBackendAvailability();
      if (!isAvailable) {
        navigate('/maintenance');
        return;
      }

      const response: any = await api.post(API_CONFIG.ENDPOINTS.AUTH.LOGIN, { email, password });
      if (!response?.success) {
        const msg = response?.message || 'Login failed';
        setError(msg);
        throw new Error(msg);
      }

      const authData = response?.data;
      const userPayload = authData?.payload?.user?.userDto || authData?.payload;
      const businesses = userPayload?.businesses || [];
      const normalized = normalizeUserData(userPayload || response);
      if (normalized) {
        setUser(normalized);
        setBusinessDetails(normalized.businesses || null);
      }
      applyPermissions(authData);

      const hasBusinesses = businesses.length > 0;

      if (opts?.redirectTo) {
        navigate(opts.redirectTo);
      } else if (normalized?.role === 'ADMIN' || normalized?.role === 'ROLE_ADMIN') {
        navigate('/admin');
      } else if (!hasBusinesses) {
        navigate('/business-setup');
      } else {
        navigate('/dashboard');
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Login failed';
      setError(msg);
      throw new Error(msg);
    } finally {
      setLoading(false);
    }
  };

  const register = async (userData: any): Promise<boolean> => {
    try {
      setError(null);
      setLoading(true);

      const isAvailable = await checkBackendAvailability();
      if (!isAvailable) {
        navigate('/maintenance');
        return false;
      }

      const response: any = await api.post(API_CONFIG.ENDPOINTS.AUTH.REGISTER, userData);
      if (!response?.success || !response?.data) {
        const msg = response?.message || 'Registration failed';
        setError(msg);
        throw new Error(msg);
      }

      const me: any = await api.get(API_CONFIG.ENDPOINTS.AUTH.ME);
      const userDataRes = me?.data;
      if (userDataRes) {
        const normalized = normalizeUserData(userDataRes);
        setUser(normalized);
        setBusinessDetails(normalized?.businesses || null);
      }
      return (userDataRes?.businesses?.length || 0) > 0;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Registration failed';
      setError(msg);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await api.post(API_CONFIG.ENDPOINTS.AUTH.LOGOUT);
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Logout API call failed:', err);
      }
    }
    setUser(null);
    setBusinessDetails(null);
    setPermissions([]);
    setPermissionLevel('USER');
    localStorage.removeItem('businessSetupCompleted');
    localStorage.removeItem('businessDetails');
    localStorage.removeItem('processedAuthResponses');
    localStorage.removeItem('invoiceData');
    localStorage.removeItem('invoiceDisplayPreferences');
    sessionStorage.removeItem('preAuthPath');
    navigate('/login');
  };

  const handleGoogleAuth = useCallback(async (apiResponse: any): Promise<UserData> => {
    let parsedResponse: any;
    if (typeof apiResponse === 'string') {
      try {
        parsedResponse = JSON.parse(decodeURIComponent(apiResponse));
      } catch {
        throw new Error('Failed to parse authentication response');
      }
    } else {
      parsedResponse = apiResponse;
    }

    let authResponse = parsedResponse?.data || parsedResponse;
    let userData: UserData | null = authResponse?.payload?.user?.userDto || authResponse?.payload || null;

    if (!userData) {
      throw new Error('Failed to extract user data');
    }

    const normalized = normalizeUserData(userData);
    setUser(normalized);
    setBusinessDetails(normalized?.businesses || null);
    applyPermissions(authResponse);
    return userData;
  }, [applyPermissions]);

  const updateUser = (userData: any) => {
    const normalized = normalizeUserData(userData);
    if (normalized) {
      setUser(normalized);
      setBusinessDetails(normalized.businesses || null);
    }
  };

  const hasPermission = useCallback((permission: string): boolean => {
    if (!permission) return true;
    return permissions.includes(permission);
  }, [permissions]);

  const value: AuthContextType = {
    user,
    userBusinessDetails,
    permissions,
    permissionLevel,
    hasPermission,
    loading,
    error,
    login,
    register,
    logout,
    setError,
    checkBackendAvailability,
    updateUser,
    handleGoogleAuth,
    isAdmin: user?.role === 'ADMIN' || user?.role === 'ROLE_ADMIN',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
